import React from "react";
import { Text } from "./text";
// import Animated from "react-native-reanimated";
import { textType, textWeight, TypographyProps } from "../../types/typography.types";
import { styled } from "@gluestack-style/react";
import type { StyleProp, TextStyle } from "react-native";

const Typography = ({ children, type, weight, uppercase, animatedProps, isAnimated, fontWeight, style, ...props }: TypographyProps) => {
	const StyledText = styled(Text, {
		fontFamily: "$heading",
		textTransform: uppercase ? "uppercase" : "none",
		userSelect: "text",
		variants: {
			type: {
				default: {
					fontSize: "$paragraphdesktop",
					lineHeight: 24,
					fontFamily: "$body",
				},
				"heading-h1": {
					fontSize: "$h1mobile",
					lineHeight: 24,
					"@md": {
						fontSize: "$h1desktop",
						lineHeight: 48,
					},
				},
				"heading-h2": {
					fontSize: "$h2mobile",
					lineHeight: 48,
					"@md": {
						fontSize: "$h2desktop",
						lineHeight: 40,
					},
				},
				"heading-h3": {
					fontSize: "$h3mobile",
					lineHeight: 24,
					"@md": {
						fontSize: "$h3desktop",
						lineHeight: 32,
					},
				},
				"heading-h4": {
					fontSize: "$h4mobile",
					lineHeight: 24,
					"@md": {
						fontSize: "$h4desktop",
						lineHeight: 32,
					},
				},
				"heading-h5": {
					fontSize: "$h5mobile",
					lineHeight: 24,
					"@md": {
						fontSize: "$h5desktop",
						lineHeight: 24,
					},
				},
				"heading-h6": {
					fontSize: "$h6mobile",
					lineHeight: 24,
					"@md": {
						fontSize: "$h6desktop",
						lineHeight: 24,
					},
				},
				paragraph: {
					fontSize: "$paragraphdesktop",
					lineHeight: 24,
					fontFamily: "$body",
				},
				"paragraph-small": {
					fontSize: "$paragraphsmalldesktop",
					lineHeight: 24,
					fontFamily: "$body",
				},
			},
			weight: {
				normal: {
					fontWeight: "normal",
				},
				bold: {
					fontFamily: "Roboto_700Bold",
				},
				semibold: {
					fontWeight: "500",
				},
			},
		},
	});

	// Ensure fontWeight in the style prop takes precedence
	const combinedStyle: StyleProp<TextStyle> = [
		style, // Original style prop from component usage
		// Apply fontWeight from props if it exists
		fontWeight !== undefined && { fontWeight: fontWeight },
	];

	if (isAnimated) {
		const { entering, exiting, layout } = animatedProps;
		return (
			// <Animated.View entering={entering} exiting={exiting} layout={layout} {...props}>
			<StyledText
				type={type ? textType[type] : "default"}
				// If fontWeight is set via props, don't apply the 'weight' variant
				weight={fontWeight !== undefined ? undefined : weight ? textWeight[weight] : "normal"}
				style={combinedStyle} // Apply combined style (including fontWeight)
				{...props}
			>
				{children}
			</StyledText>
			// </Animated.View>
		);
	}
	return (
		<StyledText
			type={type ? textType[type] : "default"}
			// If fontWeight is set via props, don't apply the 'weight' variant
			weight={fontWeight !== undefined ? undefined : weight ? textWeight[weight] : "normal"}
			style={combinedStyle} // Apply combined style (including fontWeight)
			{...props}
		>
			{children}
		</StyledText>
	);
};

export const H1 = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="heading-h1" weight="bold" {...props}>
			{children}
		</Typography>
	);
};

export const H2 = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="heading-h2" weight="bold" {...props}>
			{children}
		</Typography>
	);
};

export const H3 = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="heading-h3" weight="bold" {...props}>
			{children}
		</Typography>
	);
};

export const H4 = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="heading-h4" weight="bold" {...props}>
			{children}
		</Typography>
	);
};

export const H5 = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="heading-h5" weight="bold" {...props}>
			{children}
		</Typography>
	);
};

export const H6 = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="heading-h6" weight="bold" {...props}>
			{children}
		</Typography>
	);
};

export const Paragraph = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="paragraph" {...props}>
			{children}
		</Typography>
	);
};

export const ParagraphSmall = ({ children, ...props }: TypographyProps) => {
	return (
		<Typography type="paragraph-small" {...props}>
			{children}
		</Typography>
	);
};

Typography.H1 = H1;
Typography.H2 = H2;
Typography.H3 = H3;
Typography.H4 = H4;
Typography.H5 = H5;
Typography.H6 = H6;
Typography.Paragraph = Paragraph;
Typography.ParagraphSmall = ParagraphSmall;

export { Typography };
